import { v4 as uuidv4 } from 'uuid';

import type { FactAttributeDetail } from '../../common/utils/factAttributeUtils';

import { createSlice } from '@reduxjs/toolkit';

import { AppThunk } from '../../app/store';
import {
  getApplicationEndPoint,
  initiateReinstatementEndPoint,
  manualPiaEndPoint,
} from '../../common/api.endPoints';
import { HttpService } from '../../common/http.service';
import { IKeyValMap } from '../../common/interfaces/IKeyValMap';
import { ITask } from '../tasks/interfaces/ITask';
import { addTasks, updateTasksState } from '../tasks/slice/taskThunks';
import { TaskTypes } from '../tasks/types/taskTypes';
import { IBundledPolicyDetails } from './interfaces/IBundledPolicyDetails';
import { IFilterItem } from './interfaces/IFilterItem';

export interface BeneficiaryDetailsDto {
  name?: string;
  firstName: string;
  middleName?: string;
  lastName?: string;
  birthDate?: string;
  relationships: string;
  share: number;
}

export const detailSlice = createSlice({
  name: 'applicationDetails',

  initialState: {
    termPolicyId: 0,
    carrierPolicyNumber: '',
    creationDate: '',
    nearestAge: 0,
    age: 0,
    gender: '',
    height: 0,
    weight: 0,
    income: 0,
    birthDate: '',
    birthState: null,
    birthCountry: '',
    // coverage is being used in the approval flow. If you remove, please update the approval flow
    coverage: 0,
    // healthClass is being used in the approval flow. If you remove, please update the approval flow
    healthClass: '',
    // term is being used in the approval flow. If you remove, please update the approval flow
    term: 0,
    // tobaccoClass is being used in the approval flow. If you remove, please update the approval flow
    tobaccoClass: '',
    carrier: '',
    product: '',
    subCategory: '',
    stateAtSubmission: '',
    // tableRatingPa is being used in the approval flow. If you remove, please update the approval flow
    tableRatingPa: 0,
    caseManagerId: 0,
    caseManagerName: '',
    underwriterId: 0,
    underwriterName: '',
    carrierUnderwriterId: 0,
    carrierUnderwriterName: '',
    uwxUserId: 0,
    uwxUserName: '',
    referralUserId: 0,
    referralUserName: '',
    error: undefined,
    customerName: '',
    policyholderName: '',
    bmi: 0,
    totalCoverageInForce: 0,
    occupation: '',
    applicationStage: '',
    applicationLifecycle: '',
    applicationDetailedLifecycle: '',
    applicationLifecycleV2: '',
    applicationStageV2: '',
    reassignmentFailed: false,
    reassignmentError: '',
    reassignmentErrorType: '',
    covid30DayQuestions: undefined,
    past90DayQuestions: undefined,
    isCarrierExempted: undefined,
    lastDecisionType: '',
    activated: false,
    piaError: '',
    piaErrorType: '',
    reinstatementError: '',
    reinstatementErrorType: '',
    reinstatementFailed: false,
    applicationId: 0,
    address: '',
    isAudit: false,
    isRecommendedForRescission: false,
    experimentName: '',
    linkedPolicies: undefined,
    bundledPolicyDetails: null as IBundledPolicyDetails | null,
    isSsnHolder: undefined,
    isItinHolder: undefined,
    citizenship: '',
    // citizenshipDocumentType comes from document entity, basically from the document uploaded by the user.
    citizenshipDocumentType: [] as string[],
    // identificationDocumentType comes from the citizenship entity, this is the one that the user disclosed (userDisclosedCitizenshipDocumentType)
    identificationDocumentType: [] as string[],
    nonResidentUwControlMethod: undefined,
    householdIncome: 0,
    hasAppliedInLast12Months: undefined,
    isWeightGainOrLoss: undefined,
    substanceLastUseDate: undefined,
    marijuanaUse: undefined,
    marijuanaUseDetails: null as FactAttributeDetail[] | null,
    cbdUse: undefined,
    thcUse: undefined,
    thcDetails: undefined as FactAttributeDetail[] | null | undefined,
    hasFamilyDiseaseHistory: undefined,
    hasCriminalRecord: undefined,
    avocation: undefined,
    foreignTravel: [] as string[],
    military: undefined,
    alcoholUsage: undefined,
    alcoholUsageDetails: null as FactAttributeDetail[] | null,
    beneficiaryDetails: [] as BeneficiaryDetailsDto[],
    trlScore: undefined,
    userDisclosedVisaType: undefined,
    inUSForPast18Months: undefined,
    isAmlDocAvailable: undefined,
    isReconsiderationProfile: undefined,
    trlPreQualScore: undefined,
    subCategory: '',
  },

  reducers: {
    onApplicationFetch: (state, action) => {
      state.termPolicyId = action.payload.termPolicyId;
      state.carrierPolicyNumber = action.payload.carrierPolicyNumber;
      state.creationDate = action.payload.creationDate;
      state.nearestAge = action.payload.nearestAge;
      state.age = action.payload.age;
      state.gender = action.payload.gender;
      state.height = action.payload.height;
      state.weight = action.payload.weight;
      state.income = action.payload.income;
      state.birthDate = action.payload.birthDate;
      state.birthState = action.payload.birthState;
      state.birthCountry = action.payload.birthCountry;
      state.coverage = action.payload.coverage;
      state.healthClass = action.payload.healthClass;
      state.term = action.payload.term;
      state.tobaccoClass = action.payload.tobaccoClass;
      state.carrier = action.payload.carrier;
      state.product = action.payload.product;
      state.subCategory = action.payload.subCategory ?? '';
      state.stateAtSubmission = action.payload.stateAtSubmission;
      state.tableRatingPa = action.payload.tableRatingPa;
      state.caseManagerId = action.payload.caseManagerId;
      state.caseManagerName = action.payload.caseManagerDisplayName;
      state.underwriterId = action.payload.underwriterId;
      state.underwriterName = action.payload.underwriterDisplayName;
      state.carrierUnderwriterId = action.payload.carrierUnderwriterId;
      state.carrierUnderwriterName =
        action.payload.carrierUnderwriterDisplayName;
      state.uwxUserId = action.payload.uwxUserId;
      state.uwxUserName = action.payload.uwxUserDisplayName;
      state.referralUserId = action.payload.referralUserId;
      state.referralUserName = action.payload.referralUserDisplayName;
      state.customerName = action.payload.customerDisplayName;
      state.policyholderName = action.payload.policyholderName ?? '';
      state.applicationStage = action.payload.stage;
      state.applicationLifecycle = action.payload.lifecycle;
      state.applicationDetailedLifecycle = action.payload.detailedLifecycle;
      state.applicationLifecycleV2 = action.payload.lifecycleV2;
      state.applicationStageV2 = action.payload.stageV2;
      state.covid30DayQuestions = action.payload.covid30DayQuestions;
      state.past90DayQuestions = action.payload.past90DayQuestions;
      state.isCarrierExempted = action.payload.isCarrierExempted;
      state.lastDecisionType = action.payload.lastDecisionType;
      state.activated = action.payload.activated;
      state.applicationId = action.payload.id;
      state.address = action.payload.address;
      state.isAudit = action.payload.isAudit;
      state.isRecommendedForRescission =
        action.payload.isRecommendedForRescission;
      state.bmi = action.payload.bmi;
      state.totalCoverageInForce = action.payload.totalCoverageInForce;
      state.occupation = action.payload.occupation;
      state.experimentName = action.payload.experimentName;
      state.linkedPolicies = action.payload.linkedPolicies;
      state.bundledPolicyDetails = action.payload.bundledPolicyDetails;
      state.isSsnHolder = action.payload.isSsnHolder;
      state.isItinHolder = action.payload.isItinHolder;
      state.citizenship = action.payload.citizenship;
      state.citizenshipDocumentType = action.payload.citizenshipDocumentType;
      state.identificationDocumentType =
        action.payload.identificationDocumentType;
      state.nonResidentUwControlMethod =
        action.payload.nonResidentUwControlMethod;
      state.householdIncome = action.payload.householdIncome;
      state.hasAppliedInLast12Months = action.payload.hasAppliedInLast12Months;
      state.isWeightGainOrLoss = action.payload.isWeightGainOrLoss;
      state.substanceLastUseDate = action.payload.substanceLastUseDate;
      state.marijuanaUse = action.payload.marijuanaUse;
      state.marijuanaUseDetails = action.payload.marijuanaUseDetails ?? null;
      state.cbdUse = action.payload.cbdUse;
      state.thcUse = action.payload.thcUse;
      state.thcDetails = action.payload.thcDetails;
      state.hasFamilyDiseaseHistory = action.payload.hasFamilyDiseaseHistory;
      state.hasCriminalRecord = action.payload.hasCriminalRecord;
      state.avocation = action.payload.avocation;
      state.foreignTravel = action.payload.foreignTravel;
      state.military = action.payload.military;
      state.alcoholUsage = action.payload.alcoholUsage;
      state.alcoholUsageDetails = action.payload.alcoholUsageDetails ?? null;
      state.beneficiaryDetails = action.payload.beneficiaryDetails;
      state.trlScore = action.payload.trlScore;
      state.userDisclosedVisaType = action.payload.userDisclosedVisaType;
      state.isAmlDocAvailable = action.payload.isAmlDocAvailable;
      state.inUSForPast18Months = action.payload.inUSForPast18Months;
      state.isReconsiderationProfile = action.payload.isReconsiderationProfile;
      state.trlPreQualScore = action.payload.trlPreQualScore;
      state.subCategory = action.payload.subCategory ?? '';
    },
    onReassignSuccess: (state, action) => {
      state.caseManagerId = action.payload.caseManagerId;
      state.caseManagerName = action.payload.caseManagerName;
      state.underwriterId = action.payload.underwriterId;
      state.underwriterName = action.payload.underwriterName;
      state.carrierUnderwriterId = action.payload.carrierUnderwriterId;
      state.carrierUnderwriterName = action.payload.carrierUnderwriterName;
      state.uwxUserId = action.payload.uwxUserId;
      state.uwxUserName = action.payload.uwxUserName;
      state.referralUserId = action.payload.referralUserId;
      state.referralUserName = action.payload.referralUserName;
    },
    onReassignmentFailed: (state, action) => {
      state.reassignmentFailed = true;
      state.reassignmentError = action.payload.message;
      state.reassignmentErrorType = action.payload.type;
    },
    onApplicationStageUpdate: (state, action) => {
      state.applicationStage = action.payload.applicationStage;
      if (action.payload.applicationLifecycle) {
        state.applicationLifecycle = action.payload.applicationLifecycle;
      }
      if (action.payload.isRecommendedForRescission) {
        state.isRecommendedForRescission =
          action.payload.isRecommendedForRescission;
      }
    },
    onApplicationDecisionUpdate: (state, action) => {
      state.lastDecisionType = action.payload.lastDecisionType;
    },
    onCarrierLifecycleUpdate: (state, action) => {
      state.carrierUnderwriterId = action.payload.carrierUnderwriterId;
      state.carrierUnderwriterName =
        action.payload.carrierUnderwriterDisplayName;
    },
    onPiaSuccess: (state, action) => {
      state.applicationLifecycle = action.payload.lifecycle;
      state.applicationStage = action.payload.applicationStage;
    },
    onPiafailure: (state, action) => {
      state.piaError = action.payload.message;
      state.piaErrorType = action.payload.type;
    },
    onReinstatementSuccess: (state, action) => {
      // state.applicationLifecycle = action.payload.applicationLifecycle;
      // state.applicationStage = action.payload.applicationStage;
      state.underwriterId = action.payload.underwriterAssigned.id;
      state.underwriterName = action.payload.underwriterAssigned.name;
    },
    onReinstatementfailure: (state, action) => {
      state.reinstatementError = action.payload.message;
      state.reinstatementErrorType = action.payload.type;
      state.reinstatementFailed = true;
    },
    refreshApplicationState: (state, action) => {
      state.applicationLifecycle = action.payload.lifecycle;
      state.applicationStage = action.payload.stage;
      state.applicationDetailedLifecycle = action.payload.detailedLifecycle;
      state.applicationLifecycleV2 = action.payload.lifecycleV2;
      state.applicationStageV2 = action.payload.stageV2;
      state.isRecommendedForRescission =
        action.payload.isRecommendedForRescission;
    },
  },
});

export const manualPia =
  (applicationId: number, reason: string): AppThunk =>
  async dispatch => {
    const requestBody = { piaReason: reason };
    const response = await HttpService.getInstance().patch(
      manualPiaEndPoint(applicationId),
      JSON.stringify(requestBody),
    );

    // If successful, set application stage and life cycle
    if (response && !response.error) {
      // dispatch(detailActions.onPiaSuccess(response.parsedBody));
      dispatch(syncApplicationState(applicationId.toString()));
    } else {
      dispatch(
        detailActions.onPiafailure(response ? response.error : undefined),
      );
    }
  };

export const initiateReinstatement =
  (applicationId: number): AppThunk =>
  async dispatch => {
    const response = await HttpService.getInstance().patch(
      initiateReinstatementEndPoint(applicationId),
    );
    // If successful, set application stage and life cycle
    if (response && !response.error) {
      // dispatch(detailActions.onReinstatementSuccess(response.parsedBody));
      dispatch(syncApplicationState(applicationId.toString()));
      if (response.parsedBody.tasks) {
        const newTasks = response.parsedBody.tasks.filter(
          (task: ITask) =>
            task.taskType === TaskTypes.REINSTATEMENT_APPLICATION,
        );
        const updatedTasks = response.parsedBody.tasks.filter(
          (task: ITask) =>
            task.taskType !== TaskTypes.REINSTATEMENT_APPLICATION,
        );
        if (newTasks.length > 0) {
          dispatch(addTasks(newTasks));
        }

        if (updatedTasks.length > 0) {
          dispatch(updateTasksState(updatedTasks));
        }
      }
    } else {
      dispatch(
        detailActions.onReinstatementfailure(
          response ? response.error : undefined,
        ),
      );
    }
  };

export const reassignApplication =
  (
    applicationId: string,
    selectedUser: IFilterItem,
    requestType?: string,
  ): AppThunk =>
  async dispatch => {
    const requestBody: any = { userId: selectedUser.value };
    if (requestType) {
      requestBody.requestType = requestType;
    }
    const response = await HttpService.getInstance().patch(
      getApplicationEndPoint(applicationId),
      JSON.stringify(requestBody),
    );

    // If successful, set reassignmentFailed to true
    // Else if there's an error, set reassignment failure state
    if (response && !response.error) {
      dispatch(detailActions.onReassignSuccess(response.parsedBody));
    } else {
      dispatch(
        detailActions.onReassignmentFailed(
          response ? response.error : undefined,
        ),
      );
    }
  };

export const fetchApplication =
  (
    id: string,
    setApplicationFetchStatus: (status: string) => void,
    openSnacks: any,
    setSnacksCallback: (updatedSnacks: any) => void,
  ): AppThunk =>
  async dispatch => {
    setApplicationFetchStatus('loading');
    const data: IKeyValMap = await HttpService.getInstance().get(
      getApplicationEndPoint(id),
    );
    if (data && !data.error) {
      const application = data.parsedBody;
      dispatch(detailActions.onApplicationFetch(application));
      setApplicationFetchStatus('success');
    } else {
      setApplicationFetchStatus('error');
      if (data && data.error) {
        setSnacksCallback([
          ...openSnacks,
          {
            id: uuidv4(),
            type: data.error.type,
            message: data.error.message,
          },
        ]);
      }
    }
  };

export const syncApplicationState =
  (applicationId: string): AppThunk =>
  async dispatch => {
    const data: IKeyValMap = await HttpService.getInstance().get(
      getApplicationEndPoint(applicationId),
    );

    if (data && !data.error) {
      const application = data.parsedBody;
      dispatch(
        detailActions.refreshApplicationState({
          lifecycle: application.lifecycle,
          stage: application.stage,
          detailedLifecycle: application.detailedLifecycle,
          lifecycleV2: application.lifecycleV2,
          stageV2: application.stageV2,
          isRecommendedForRescission: application.isRecommendedForRescission,
        }),
      );
    }
  };

export const detailReducer = detailSlice.reducer;
export const detailActions = detailSlice.actions;
